import { useRef, useState, useEffect } from 'react';

interface WeightInputProps {
  className?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const WeightInput = ({ className, value, onChange, placeholder = 'Enter weight' }: WeightInputProps) => {
  const formatWeight = (input: string) => {
    // Remove all non-digit characters
    const digits = input.replace(/[^\d]/g, '');

    if (digits.length === 0) return '';
    return `${digits} lb`;
  };

  const [displayValue, setDisplayValue] = useState(() => formatWeight(value));
  const inputRef = useRef(null);

  // Update displayValue when value prop changes (e.g., from scrolling)
  useEffect(() => {
    const formatted = formatWeight(value);
    setDisplayValue(formatted);
  }, [value]);

  const parseWeight = (formatted: string) => {
    // Extract just the digits for the actual value
    return formatted.replace(/[^\d]/g, '');
  };

  const getCursorPositionInDigits = (cursorPos: number, formattedValue: string) => {
    // Convert cursor position in formatted string to position in digits-only string
    let digitPos = 0;
    for (let i = 0; i < cursorPos && i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitPos++;
      }
    }
    return digitPos;
  };

  const getCursorPositionInFormatted = (digitPos: number, formattedValue: string) => {
    // Convert digit position back to formatted string position
    let currentDigitPos = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        if (currentDigitPos === digitPos) return i;
        currentDigitPos++;
      }
    }
    // If we're at the end of digits, place cursor before " lbs"
    const lbsIndex = formattedValue.indexOf(' lb');
    return lbsIndex > -1 ? lbsIndex : formattedValue.length;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    // If input is empty or only contains formatting characters, clear everything
    const digitsOnly = input.replace(/[^\d]/g, '');
    if (digitsOnly.length === 0) {
      setDisplayValue('');
      if (onChange) {
        onChange('');
      }
      return;
    }

    // Store cursor position relative to digits in the current input
    const digitPos = getCursorPositionInDigits(cursorPos, input);

    const formatted = formatWeight(input);
    const parsed = parseWeight(formatted);

    setDisplayValue(formatted);

    // Restore cursor position
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = getCursorPositionInFormatted(digitPos, formatted);
        (inputRef.current as HTMLInputElement).setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    // Call parent onChange with clean numeric value
    if (onChange) {
      onChange(parsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow all control keys and navigation
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    // Handle Enter key
    if (e.key === 'Enter' || e.keyCode === 13) {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }

    // Special handling for backspace
    if (e.key === 'Backspace') {
      const input = e.currentTarget;
      const cursorPos = input.selectionStart || 0;
      const cursorEnd = input.selectionEnd || 0;

      // If there's a selection, let default behavior handle it
      if (cursorPos !== cursorEnd) return;

      // If cursor is at the beginning, prevent backspace
      if (cursorPos === 0) {
        e.preventDefault();
        return;
      }

      // If we're about to delete part of " lb", delete the last digit instead
      const charBefore = displayValue[cursorPos - 1];
      if (charBefore && (charBefore === ' ' || charBefore === 'l' || charBefore === 'b')) {
        e.preventDefault();
        // Find the last digit and remove it
        const digitsOnly = displayValue.replace(/[^\d]/g, '');
        if (digitsOnly.length > 0) {
          const newDigits = digitsOnly.slice(0, -1);
          const formatted = formatWeight(newDigits);
          setDisplayValue(formatted);
          if (onChange) {
            onChange(newDigits);
          }
          // Position cursor before " lb"
          setTimeout(() => {
            if (inputRef.current) {
              const lbIndex = formatted.indexOf(' lb');
              const newPos = lbIndex > -1 ? lbIndex : formatted.length;
              (inputRef.current as HTMLInputElement).setSelectionRange(newPos, newPos);
            }
          }, 0);
        }
        return;
      }
    }

    // Allow special keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
    ];

    if (allowedKeys.includes(e.key)) return;

    // Only allow digits (0-9)
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      maxLength={7} // Limits to something like "999 lbs"
    />
  );
};
